import { useState, useEffect } from 'react';
import axios from 'axios';
import AdminLayout from '@/components/AdminLayout';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Save, Loader2, Info, Users, Plus, Trash2, Edit, GripVertical, HelpCircle, Handshake, ExternalLink, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import { API } from '@/App';

const InstitutionalCMSPage = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState('about');

  // About content
  const [about, setAbout] = useState({
    title: 'Qui sommes-nous ?',
    paragraphs: [],
    closing: '',
    stat_regions: 12,
    stat_years: 25,
  });

  // Bureau content
  const [bureau, setBureau] = useState({
    intro_text: '',
    members: [],
    council_title: "Le conseil d'administration",
    council_members: [],
  });

  // FAQ content
  const [faqItems, setFaqItems] = useState([]);
  const [faqModal, setFaqModal] = useState(false);
  const [editingFaq, setEditingFaq] = useState(null);
  const [faqForm, setFaqForm] = useState({ category: '', question: '', answer: '', order: 0, is_active: true });

  // Partners content
  const [partners, setPartners] = useState([]);
  const [partnerModal, setPartnerModal] = useState(false);
  const [editingPartner, setEditingPartner] = useState(null);
  const [partnerForm, setPartnerForm] = useState({ name: '', description: '', logo_url: '', website_url: '', type: 'National', order: 0, is_active: true });

  // Member modal
  const [memberModal, setMemberModal] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [memberType, setMemberType] = useState('bureau');
  const [memberForm, setMemberForm] = useState({ name: '', role: '', photo_url: '', order: 0 });

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => { fetchContent(); }, []);

  const fetchContent = async () => {
    try {
      const [siteRes, faqRes, partnersRes] = await Promise.all([
        axios.get(`${API}/site-content`),
        axios.get(`${API}/faq`),
        axios.get(`${API}/partners`),
      ]);
      const data = {};
      siteRes.data.forEach(item => { data[item.section] = item.data || item; });
      if (data.about) setAbout(prev => ({ ...prev, ...data.about }));
      if (data.bureau) setBureau(prev => ({ ...prev, ...data.bureau }));
      setFaqItems(faqRes.data);
      setPartners(partnersRes.data);
    } catch { /* defaults */ }
    setLoading(false);
  };

  const saveSection = async (section) => {
    setSaving(true);
    try {
      const payload = section === 'about' ? about : bureau;
      await axios.put(`${API}/site-content/${section}`, { data: payload }, { headers });
      toast.success(`Section "${section}" sauvegardee`);
    } catch { toast.error('Erreur de sauvegarde'); }
    setSaving(false);
  };

  // About paragraph management
  const addParagraph = () => setAbout(prev => ({ ...prev, paragraphs: [...prev.paragraphs, ''] }));
  const updateParagraph = (i, val) => {
    const p = [...about.paragraphs]; p[i] = val;
    setAbout(prev => ({ ...prev, paragraphs: p }));
  };
  const removeParagraph = (i) => {
    setAbout(prev => ({ ...prev, paragraphs: prev.paragraphs.filter((_, idx) => idx !== i) }));
  };

  // Bureau member management
  const saveMember = () => {
    if (memberType === 'bureau') {
      if (editingMember !== null) {
        const m = [...bureau.members]; m[editingMember] = memberForm;
        setBureau(prev => ({ ...prev, members: m }));
      } else {
        setBureau(prev => ({ ...prev, members: [...prev.members, memberForm] }));
      }
    } else {
      if (editingMember !== null) {
        const m = [...bureau.council_members]; m[editingMember] = memberForm;
        setBureau(prev => ({ ...prev, council_members: m }));
      } else {
        setBureau(prev => ({ ...prev, council_members: [...prev.council_members, memberForm] }));
      }
    }
    setMemberModal(false);
    toast.success('Membre ajoute');
  };
  const removeMember = (type, idx) => {
    if (!window.confirm('Retirer ce membre ?')) return;
    if (type === 'bureau') setBureau(prev => ({ ...prev, members: prev.members.filter((_, i) => i !== idx) }));
    else setBureau(prev => ({ ...prev, council_members: prev.council_members.filter((_, i) => i !== idx) }));
  };
  const openMemberEdit = (type, idx) => {
    const list = type === 'bureau' ? bureau.members : bureau.council_members;
    setMemberType(type); setEditingMember(idx); setMemberForm(list[idx]); setMemberModal(true);
  };

  // FAQ management
  const saveFaq = async () => {
    setSaving(true);
    try {
      if (editingFaq) {
        await axios.put(`${API}/faq/${editingFaq}`, faqForm, { headers });
        toast.success('FAQ mise a jour');
      } else {
        await axios.post(`${API}/faq`, faqForm, { headers });
        toast.success('FAQ ajoutee');
      }
      setFaqModal(false);
      fetchContent();
    } catch { toast.error('Erreur'); }
    setSaving(false);
  };
  const deleteFaq = async (id) => {
    if (!window.confirm('Supprimer cette FAQ ?')) return;
    try {
      await axios.delete(`${API}/faq/${id}`, { headers });
      setFaqItems(prev => prev.filter(f => f.id !== id));
      toast.success('FAQ supprimée');
    } catch { toast.error('Erreur'); }
  };
  const openFaqEdit = (faq) => {
    setEditingFaq(faq.id);
    setFaqForm({ category: faq.category, question: faq.question, answer: faq.answer, order: faq.order || 0, is_active: faq.is_active !== false });
    setFaqModal(true);
  };

  // Partners management
  const savePartner = async () => {
    setSaving(true);
    try {
      if (editingPartner) {
        await axios.put(`${API}/partners/${editingPartner}`, partnerForm, { headers });
        toast.success('Partenaire mis a jour');
      } else {
        await axios.post(`${API}/partners`, partnerForm, { headers });
        toast.success('Partenaire ajoute');
      }
      setPartnerModal(false);
      fetchContent();
    } catch { toast.error('Erreur'); }
    setSaving(false);
  };
  const deletePartner = async (id) => {
    if (!window.confirm('Supprimer ce partenaire ?')) return;
    try {
      await axios.delete(`${API}/partners/${id}`, { headers });
      setPartners(prev => prev.filter(p => p.id !== id));
      toast.success('Partenaire supprime');
    } catch { toast.error('Erreur'); }
  };
  const openPartnerEdit = (p) => {
    setEditingPartner(p.id);
    setPartnerForm({ name: p.name, description: p.description, logo_url: p.logo_url || '', website_url: p.website_url || '', type: p.type || 'National', order: p.order || 0, is_active: p.is_active !== false });
    setPartnerModal(true);
  };

  const inputClass = 'w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:border-[#35AFE4] focus:ring-2 focus:ring-[#35AFE4]/10 outline-none';
  const labelClass = 'block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5';

  const tabs = [
    { id: 'about', label: 'Qui sommes-nous', icon: Info },
    { id: 'faq', label: 'FAQ', icon: HelpCircle },
  ];

  const faqCategories = [...new Set(faqItems.map(f => f.category))];

  if (loading) return <AdminLayout><div className="flex items-center justify-center h-64"><Loader2 className="animate-spin text-gray-400" size={24} /></div></AdminLayout>;

  return (
    <AdminLayout>
      <div className="p-4 lg:p-8 min-h-full" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="institutional-cms">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-xl lg:text-2xl font-bold text-gray-900 tracking-tight">Pages Institutionnelles</h1>
            <p className="text-sm text-gray-400 mt-0.5">Modifiez le contenu des pages publiques</p>
          </div>
          {(tab === 'about') && (
            <Button onClick={() => saveSection(tab)} disabled={saving} className="bg-[#1e3a5f] hover:bg-[#162d4a] text-white" data-testid="save-btn">
              {saving ? <Loader2 size={16} className="animate-spin mr-1.5" /> : <Save size={16} className="mr-1.5" />}
              Sauvegarder
            </Button>
          )}
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mb-6 bg-gray-100 rounded-xl p-1 w-fit flex-wrap">
          {tabs.map(t => {
            const Icon = t.icon;
            return (
              <button key={t.id} onClick={() => setTab(t.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all ${tab === t.id ? 'bg-white text-[#1e3a5f] shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                data-testid={`tab-${t.id}`}
              >
                <Icon size={16} /> {t.label}
              </button>
            );
          })}
        </div>

        {/* ===== ABOUT TAB ===== */}
        {tab === 'about' && (
          <div className="space-y-6">
            <div className="bg-white rounded-2xl border border-gray-100 p-6">
              <h2 className="font-bold text-gray-900 mb-4">Titre de la section</h2>
              <input value={about.title} onChange={e => setAbout({...about, title: e.target.value})} className={inputClass} data-testid="about-title" />
            </div>
            <div className="bg-white rounded-2xl border border-gray-100 p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-bold text-gray-900">Paragraphes</h2>
                <button onClick={addParagraph} className="flex items-center gap-1 text-sm text-[#35AFE4] font-semibold hover:text-[#1e3a5f]">
                  <Plus size={14} /> Ajouter un paragraphe
                </button>
              </div>
              <div className="space-y-3">
                {about.paragraphs.map((p, i) => (
                  <div key={i} className="flex gap-2">
                    <GripVertical size={16} className="text-gray-300 mt-3 flex-shrink-0" />
                    <Textarea value={p} onChange={e => updateParagraph(i, e.target.value)} rows={3} className="flex-1" />
                    <button onClick={() => removeParagraph(i)} className="p-2 text-gray-400 hover:text-red-500 flex-shrink-0"><Trash2 size={16} /></button>
                  </div>
                ))}
                {about.paragraphs.length === 0 && <p className="text-sm text-gray-400 italic">Aucun paragraphe. Cliquez sur "Ajouter" pour commencer.</p>}
              </div>
            </div>
            <div className="bg-white rounded-2xl border border-gray-100 p-6">
              <h2 className="font-bold text-gray-900 mb-4">Phrase de conclusion</h2>
              <input value={about.closing} onChange={e => setAbout({...about, closing: e.target.value})} className={inputClass} placeholder="Alors gardons le contact..." data-testid="about-closing" />
            </div>
            <div className="bg-white rounded-2xl border border-gray-100 p-6">
              <h2 className="font-bold text-gray-900 mb-4">Chiffres cles</h2>
              <div className="grid grid-cols-2 gap-4">
                <div><label className={labelClass}>Nombre de Régions</label><input type="number" value={about.stat_regions} onChange={e => setAbout({...about, stat_regions: parseInt(e.target.value) || 0})} className={inputClass} /></div>
                <div><label className={labelClass}>Années d'existence</label><input type="number" value={about.stat_years} onChange={e => setAbout({...about, stat_years: parseInt(e.target.value) || 0})} className={inputClass} /></div>
              </div>
            </div>
          </div>
        )}

        {/* ===== FAQ TAB ===== */}
        {tab === 'faq' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <p className="text-sm text-gray-500">{faqItems.length} question(s) au total</p>
              <Button onClick={() => { setEditingFaq(null); setFaqForm({ category: faqCategories[0] || 'Général', question: '', answer: '', order: faqItems.length + 1, is_active: true }); setFaqModal(true); }}
                className="bg-[#1e3a5f] hover:bg-[#162d4a] text-white" data-testid="add-faq-btn">
                <Plus size={16} className="mr-1.5" /> Ajouter une question
              </Button>
            </div>

            {faqCategories.map(cat => (
              <div key={cat} className="bg-white rounded-2xl border border-gray-100 p-6">
                <h2 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-[#35AFE4]" />
                  {cat}
                </h2>
                <div className="space-y-2">
                  {faqItems.filter(f => f.category === cat).map(faq => (
                    <div key={faq.id} className={`flex items-start gap-3 p-3 rounded-xl border ${faq.is_active === false ? 'border-gray-100 bg-gray-50 opacity-60' : 'border-gray-100 hover:border-gray-200'} transition-all`} data-testid={`faq-admin-${faq.id}`}>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-sm text-gray-800">{faq.question}</p>
                        <p className="text-xs text-gray-500 mt-1 line-clamp-2">{faq.answer}</p>
                      </div>
                      <div className="flex gap-1 flex-shrink-0">
                        {faq.is_active === false ? <EyeOff size={14} className="text-gray-400 mt-1" /> : <Eye size={14} className="text-green-500 mt-1" />}
                        <button onClick={() => openFaqEdit(faq)} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400"><Edit size={14} /></button>
                        <button onClick={() => deleteFaq(faq.id)} className="p-1.5 rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-500"><Trash2 size={14} /></button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
            {faqItems.length === 0 && (
              <div className="bg-white rounded-2xl border border-gray-100 p-12 text-center">
                <HelpCircle size={32} className="text-gray-300 mx-auto mb-3" />
                <p className="text-sm text-gray-400">Aucune FAQ. Cliquez sur "Ajouter" pour commencer.</p>
              </div>
            )}
          </div>
        )}

        {/* ===== PARTNERS TAB ===== */}
        {tab === 'partners' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <p className="text-sm text-gray-500">{partners.length} partenaire(s)</p>
              <Button onClick={() => { setEditingPartner(null); setPartnerForm({ name: '', description: '', logo_url: '', website_url: '', type: 'National', order: partners.length + 1, is_active: true }); setPartnerModal(true); }}
                className="bg-[#1e3a5f] hover:bg-[#162d4a] text-white" data-testid="add-partner-btn">
                <Plus size={16} className="mr-1.5" /> Ajouter un partenaire
              </Button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {partners.map(p => (
                <div key={p.id} className={`bg-white rounded-2xl border border-gray-100 p-5 hover:shadow-sm transition-all ${p.is_active === false ? 'opacity-50' : ''}`} data-testid={`partner-admin-${p.id}`}>
                  <div className="flex items-start justify-between mb-3">
                    <div className="w-16 h-16 rounded-xl bg-gray-50 flex items-center justify-center p-2">
                      {p.logo_url ? <img src={p.logo_url} alt={p.name} className="max-w-full max-h-full object-contain" /> : <Handshake size={24} className="text-gray-400" />}
                    </div>
                    <div className="flex gap-1">
                      <button onClick={() => openPartnerEdit(p)} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400"><Edit size={14} /></button>
                      <button onClick={() => deletePartner(p.id)} className="p-1.5 rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-500"><Trash2 size={14} /></button>
                    </div>
                  </div>
                  <h3 className="font-bold text-sm text-gray-900">{p.name}</h3>
                  <p className="text-xs text-gray-500 mt-1">{p.description}</p>
                  <div className="mt-3 flex items-center gap-2">
                    <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#eaf6fc] text-[#35AFE4]">{p.type}</span>
                    {p.website_url && p.website_url !== '#' && <ExternalLink size={12} className="text-gray-400" />}
                  </div>
                </div>
              ))}
            </div>

            {partners.length === 0 && (
              <div className="bg-white rounded-2xl border border-gray-100 p-12 text-center">
                <Handshake size={32} className="text-gray-300 mx-auto mb-3" />
                <p className="text-sm text-gray-400">Aucun partenaire. Cliquez sur "Ajouter" pour commencer.</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ===== MEMBER MODAL ===== */}
      <Dialog open={memberModal} onOpenChange={setMemberModal}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{editingMember !== null ? 'Modifier le membre' : 'Ajouter un membre'}</DialogTitle></DialogHeader>
          <div className="space-y-4 pt-2">
            <div><label className={labelClass}>Nom complet</label><input value={memberForm.name} onChange={e => setMemberForm({...memberForm, name: e.target.value})} className={inputClass} placeholder="Dr Aurore MUSELIER MATHIEU" data-testid="member-name" /></div>
            <div><label className={labelClass}>Fonction</label><input value={memberForm.role} onChange={e => setMemberForm({...memberForm, role: e.target.value})} className={inputClass} placeholder="Presidente" /></div>
            <div><label className={labelClass}>Photo URL</label><input value={memberForm.photo_url} onChange={e => setMemberForm({...memberForm, photo_url: e.target.value})} className={inputClass} placeholder="https://..." /></div>
            <div><label className={labelClass}>Ordre d'affichage</label><input type="number" value={memberForm.order} onChange={e => setMemberForm({...memberForm, order: parseInt(e.target.value) || 0})} className={inputClass} /></div>
            <Button onClick={saveMember} className="w-full bg-[#1e3a5f] hover:bg-[#162d4a] text-white" data-testid="save-member">
              {editingMember !== null ? 'Sauvegarder' : 'Ajouter'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ===== FAQ MODAL ===== */}
      <Dialog open={faqModal} onOpenChange={setFaqModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{editingFaq ? 'Modifier la FAQ' : 'Ajouter une FAQ'}</DialogTitle></DialogHeader>
          <div className="space-y-4 pt-2">
            <div><label className={labelClass}>Catégorie</label><input value={faqForm.category} onChange={e => setFaqForm({...faqForm, category: e.target.value})} className={inputClass} placeholder="Adhesion et Compte" data-testid="faq-category-input" list="faq-cats" /><datalist id="faq-cats">{faqCategories.map(c => <option key={c} value={c} />)}</datalist></div>
            <div><label className={labelClass}>Question</label><input value={faqForm.question} onChange={e => setFaqForm({...faqForm, question: e.target.value})} className={inputClass} placeholder="Comment devenir membre ?" data-testid="faq-question-input" /></div>
            <div><label className={labelClass}>Réponse</label><Textarea value={faqForm.answer} onChange={e => setFaqForm({...faqForm, answer: e.target.value})} rows={4} placeholder="Pour devenir membre..." data-testid="faq-answer-input" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><label className={labelClass}>Ordre</label><input type="number" value={faqForm.order} onChange={e => setFaqForm({...faqForm, order: parseInt(e.target.value) || 0})} className={inputClass} /></div>
              <div><label className={labelClass}>Visible</label>
                <button onClick={() => setFaqForm(prev => ({...prev, is_active: !prev.is_active}))} className={`w-full px-4 py-2.5 rounded-xl border text-sm font-semibold ${faqForm.is_active ? 'border-green-200 bg-green-50 text-green-700' : 'border-gray-200 bg-gray-50 text-gray-500'}`}>
                  {faqForm.is_active ? 'Visible' : 'Masque'}
                </button>
              </div>
            </div>
            <Button onClick={saveFaq} disabled={saving} className="w-full bg-[#1e3a5f] hover:bg-[#162d4a] text-white" data-testid="save-faq">
              {saving ? <Loader2 size={16} className="animate-spin mr-1.5" /> : null}
              {editingFaq ? 'Sauvegarder' : 'Ajouter'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ===== PARTNER MODAL ===== */}
      <Dialog open={partnerModal} onOpenChange={setPartnerModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{editingPartner ? 'Modifier le partenaire' : 'Ajouter un partenaire'}</DialogTitle></DialogHeader>
          <div className="space-y-4 pt-2">
            <div><label className={labelClass}>Nom</label><input value={partnerForm.name} onChange={e => setPartnerForm({...partnerForm, name: e.target.value})} className={inputClass} placeholder="SFO" data-testid="partner-name-input" /></div>
            <div><label className={labelClass}>Description</label><input value={partnerForm.description} onChange={e => setPartnerForm({...partnerForm, description: e.target.value})} className={inputClass} placeholder="Société Francaise d'Ophtalmologie" /></div>
            <div><label className={labelClass}>URL du logo</label><input value={partnerForm.logo_url} onChange={e => setPartnerForm({...partnerForm, logo_url: e.target.value})} className={inputClass} placeholder="https://..." /></div>
            <div><label className={labelClass}>Site web</label><input value={partnerForm.website_url} onChange={e => setPartnerForm({...partnerForm, website_url: e.target.value})} className={inputClass} placeholder="https://www.sfo.asso.fr" /></div>
            <div className="grid grid-cols-3 gap-4">
              <div><label className={labelClass}>Type</label>
                <select value={partnerForm.type} onChange={e => setPartnerForm({...partnerForm, type: e.target.value})} className={inputClass}>
                  <option value="National">National</option>
                  <option value="Europeen">Europeen</option>
                  <option value="International">International</option>
                  <option value="Industrie">Industrie</option>
                </select>
              </div>
              <div><label className={labelClass}>Ordre</label><input type="number" value={partnerForm.order} onChange={e => setPartnerForm({...partnerForm, order: parseInt(e.target.value) || 0})} className={inputClass} /></div>
              <div><label className={labelClass}>Visible</label>
                <button onClick={() => setPartnerForm(prev => ({...prev, is_active: !prev.is_active}))} className={`w-full px-4 py-2.5 rounded-xl border text-sm font-semibold ${partnerForm.is_active ? 'border-green-200 bg-green-50 text-green-700' : 'border-gray-200 bg-gray-50 text-gray-500'}`}>
                  {partnerForm.is_active ? 'Visible' : 'Masque'}
                </button>
              </div>
            </div>
            <Button onClick={savePartner} disabled={saving} className="w-full bg-[#1e3a5f] hover:bg-[#162d4a] text-white" data-testid="save-partner">
              {saving ? <Loader2 size={16} className="animate-spin mr-1.5" /> : null}
              {editingPartner ? 'Sauvegarder' : 'Ajouter'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default InstitutionalCMSPage;
